/**
 * `SceneTreePanel` — WP-2 (Content catalog and scene tree refinement).
 *
 * Single source of truth for the gTwin's content catalog. Renders the
 * unified scene-index tree built from the `scene-package.json` +
 * `scene-index.json` contracts.
 *
 * ## Slot / kind taxonomy (do not invent local names)
 *
 * The renderable tree is built from two binding contracts in
 * `app-platform-gtwin/docs/`:
 *
 *  - `SCENE_CONTRACT.md` — defines the six **slots** the viewer must
 *    respect: `basemaps`, `terrain`, `realityData`, `engineeringModels`,
 *    `featureLayers`, `overlays`. Each `SceneItem` carries a `layerKind`
 *    drawn from `LayerKind` (`raster`, `vector`, `bim`, `pointcloud`,
 *    `3dtiles`, `terrain`, `sensor`/`timeseries`, `panorama`,
 *    `basemap`).
 *  - `SCENE_INDEX_CONTRACT.md` — defines the index **node kinds**:
 *    `scene`, `group`, `layer`, `feature`, `evidence`, `integration`,
 *    `readiness-gap`, `ai-candidate`. The unified tree merges the two
 *    so a node can simultaneously expose `sceneItemIds` (for the
 *    renderable layer) and `geometryRefs[].featureId` (for inner
 *    features inside a layer).
 *
 * Every per-row affordance below maps onto one of those contract names;
 * we never invent a local taxonomy.
 *
 * ## What this panel adds in WP-2
 *
 * - **Contextual menu** per node (right-click, keyboard `F10` /
 *   `Shift+F10` / `ContextMenu`, or the `more_vert` row button) with
 *   four canonical actions: *Open layer tools*, *Remove from project*,
 *   *Edit metadata*, *Save view of this layer*. Each action persists
 *   into the gTwin via `gtwinClient.scene.*` when WP-1's SDK surface is
 *   available (feature-detected; falls back to the in-memory store for
 *   the developer mock today).
 * - **Per-node readiness chip** (`visualization-ready`, `asset-ready`,
 *   `simulation-ready`, …) using semantic tokens **and** a non-color
 *   icon badge (`visibility`, `verified`, `check_circle`, `warning`,
 *   `help`) so the state is communicated without color alone
 *   (DESIGN_POLICY.md §5).
 * - **Expand / collapse all** buttons (deep) + **Show only visible**
 *   filter chip.
 * - **Roving tabindex** with arrow-key navigation + `aria-selected` /
 *   `aria-expanded` per `treeitem`.
 *
 * Selection writes route through `app/sceneSelection.ts` so future
 * ToolCards (WPs 3..10) can consume the same shape.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import type { Layer } from "@geosoft/viewer-sdk";
import {
  Chip,
  Icon,
  Menu,
  tokens,
  type ChipVariant,
  type IconName,
  type MenuItemDef,
} from "@/design";
import type {
  IntegrationOperationalState,
  SceneReadinessNotice,
  UnifiedSceneTreeNode,
} from "./scenePackage";
import { filterSceneTree, semanticKindLabel } from "./sceneExplorer";
import {
  isPanoramaBubbleNode,
  isPanoramaBubbleStationNode,
  PANORAMA_BUBBLE_NODE_ICON,
  readPanoramaBubbleStationId,
} from "./sceneTreePanoramaBubble";
import {
  metadataChipsFor,
  SLOT_LABEL,
  SLOT_ORDER,
  isSceneSlot,
  type SceneSlotName,
} from "./layerMetadata";
import {
  dispatchToolCardRequest,
  selectFromTreeNode,
  type SceneSelection,
  type ToolCardRequestDetail,
} from "./sceneSelection";
import { LayerHealthDot } from "@/components/layers/LayerHealthDot";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface SceneTreePanelProps {
  readonly tree: UnifiedSceneTreeNode;
  readonly notices: readonly SceneReadinessNotice[];
  readonly layers: readonly Layer[];
  readonly visibleIds: ReadonlySet<string>;
  readonly layerOpacity: Readonly<Record<string, number>>;
  readonly selectedNodeId?: string | null;
  readonly onSelectNode?: (node: UnifiedSceneTreeNode) => void;
  readonly onSetVisible: (id: string, visible: boolean) => void;
  readonly onSetOpacity: (id: string, opacity: number) => void;
  readonly onFlyTo: (layer: Layer) => void;

  // ---- WP-2 additions ----------------------------------------------------

  /**
   * "Open layer tools" handler. Defaults to setting the scene
   * selection in `useViewerStore` and dispatching
   * `viewer:tool-card-request`. Override from the host if you want to
   * route the activation through a different bus.
   */
  readonly onOpenLayerTools?: (
    selection: SceneSelection,
    detail: ToolCardRequestDetail | null,
  ) => void;

  /**
   * "Remove from project" handler. Defaults to calling the WP-1 SDK
   * surface `gtwinClient.scene.items.remove` when present (falls back
   * to a TODO comment + best-effort store removal so the UI keeps
   * working against the in-memory mock until WP-1 lands).
   */
  readonly onRemoveLayer?: (
    node: UnifiedSceneTreeNode,
    layer: Layer | null,
  ) => void;

  /** "Edit metadata" handler. Defaults to selecting the node so the
   *  semantic property card can switch to its edit affordance. */
  readonly onEditMetadata?: (
    node: UnifiedSceneTreeNode,
    layer: Layer | null,
  ) => void;

  /** "Save view of this layer" handler. Defaults to a no-op when no
   *  saved-view writer is wired (WP-1 owns the gTwin scene-preset
   *  upsert; this hook lets WP-1 deliver the persisted version). */
  readonly onSaveLayerView?: (
    node: UnifiedSceneTreeNode,
    layer: Layer | null,
  ) => void;

  /**
   * SSR-test seam: render the contextual menu open for a specific node
   * id without a click event. Production callers should leave this
   * undefined — the menu opens on right-click, F10 / Shift+F10 /
   * ContextMenu key, or the `more_vert` row button.
   */
  readonly defaultMenuOpenForNodeId?: string | null;

  /** SSR-test seam: start with the "Show only visible" filter on. */
  readonly defaultShowOnlyVisible?: boolean;

  /**
   * Compact project rail mode. The full WP-2 tree exposes every row
   * action inline; project-viewer default keeps the left rail navigational
   * and lets the right ToolCardDock own tools/details.
   */
  readonly compact?: boolean;

  /** Handler for "Add layer" button shown in compact mode header. */
  readonly onAddLayer?: () => void;

  // ---- Wave-1 additions --------------------------------------------------

  /** Per-layer health from the active 3D engine (drives the status dot). */
  readonly layerHealth?: Readonly<Record<string, 'ready' | 'loading' | 'failed' | 'unsupported' | 'deferred'>>;

  /** Map of layer-id → scene slot (basemaps / terrain / realityData / …).
   *  When provided, the grouped-by-slot list is rendered. */
  readonly slotMap?: Readonly<Record<string, string>>;

  /** Wave-1 A3 — animate camera to the layer's extent. */
  readonly onZoomToLayer?: (layerId: string) => void;

  /** Wave-1 B1 — current raster paint state, keyed by layer id and
   *  MapLibre paint prop name (`raster-brightness-max`, `raster-contrast`,
   *  `raster-saturation`). */
  readonly rasterPaint?: Readonly<Record<string, Record<string, number>>>;

  /** Wave-1 B1 — change handler for raster paint sliders. */
  readonly onSetRasterPaint?: (layerId: string, prop: string, value: number) => void;

  /** Wave-2 C3 — bulk visibility helper for grouped cards. */
  readonly onSetManyVisible?: (
    ids: ReadonlyArray<string>,
    visible: boolean,
  ) => void;

  /** Wave-2 C3 — keep only one layer visible. */
  readonly onSoloLayer?: (id: string) => void;

  /** Wave-2 A4 — reorder layer within card list. */
  readonly onMoveLayer?: (id: string, direction: -1 | 1) => void;

  /** Wave-2 B8 — point-cloud appearance controls. */
  readonly onSetPointCloudAppearance?: (
    layerId: string,
    patch: {
      pointBudget?: number;
      pointSize?: number;
      colorMode?: "auto" | "rgb" | "intensity" | "classification" | "elevation";
    },
  ) => void;

  /** Wave-2 B9 — point-cloud ASPRS class filter. */
  readonly onSetPointCloudClassificationFilter?: (
    layerId: string,
    classes: ReadonlySet<number>,
  ) => void;

  /** Wave-2 B10 — vector style updates for MapLibre. */
  readonly onSetVectorStyle?: (
    layerId: string,
    delta: {
      fillColor?: string;
      fillOpacity?: number;
      lineColor?: string;
      lineWidth?: number;
    },
  ) => void;

  /** Wave-2 B12 — vector labels toggle + field picker. */
  readonly onSetVectorLabels?: (
    layerId: string,
    options: { enabled: boolean; field: string | null },
  ) => void;

  /** Wave-2 C2 — compact panel preset chips. */
  readonly compactPresetSwitcher?: ReactNode;

  /** Wave-2 C8 — compact panel saved-views control. */
  readonly compactSavedViewsControl?: ReactNode;

  /** LoR7 — active panorama-bubble station id for tree highlight sync. */
  readonly activePanoramaStationId?: string | null;

  /** Open the panorama-bubble 360° viewer for a specific layer. */
  readonly onOpenPanorama?: (layer: Layer) => void;
}

// ---------------------------------------------------------------------------
// Readiness chip taxonomy
// ---------------------------------------------------------------------------

/**
 * Map readiness state names defined in `SCENE_CONTRACT.md` and
 * `SCENE_INDEX_CONTRACT.md` onto a `(variant, icon, label)` triple.
 * Color is for fast scanning; the icon is the **non-color cue** the
 * design policy mandates.
 */
function readinessAppearance(state: string): {
  variant: ChipVariant;
  icon: IconName;
  label: string;
} {
  const normalized = state.trim().toLowerCase();
  if (normalized === "visualization-ready") {
    return { variant: "info", icon: "visibility", label: state };
  }
  if (normalized === "asset-ready") {
    return { variant: "success", icon: "verified", label: state };
  }
  if (normalized === "simulation-ready") {
    return { variant: "success", icon: "check_circle", label: state };
  }
  if (normalized === "operation-ready") {
    return { variant: "success", icon: "check_circle", label: state };
  }
  if (normalized === "query-ready") {
    return { variant: "info", icon: "fact_check", label: state };
  }
  if (
    normalized === "needs-review" ||
    normalized === "low-confidence" ||
    normalized === "stale"
  ) {
    return { variant: "warning", icon: "warning", label: state };
  }
  if (
    normalized === "blocking" ||
    normalized === "failed" ||
    normalized === "unavailable"
  ) {
    return { variant: "error", icon: "error", label: state };
  }
  // Default: surface the unknown state without claiming success or
  // failure, but still communicate "non-default" with a help glyph.
  return { variant: "neutral", icon: "help", label: state };
}

function formatCount(value: unknown): string | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return `${Math.round(value)}`;
}

function formatBytes(value: unknown): string | null {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) return null;
  const units = ["B", "KB", "MB", "GB", "TB"];
  let amount = value;
  let unit = 0;
  while (amount >= 1024 && unit < units.length - 1) {
    amount /= 1024;
    unit += 1;
  }
  return `${amount >= 10 ? amount.toFixed(0) : amount.toFixed(1)} ${units[unit]}`;
}

function readRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function layerMetricChips(layer: Layer | null): Array<{
  label: string;
  icon: IconName;
  variant: ChipVariant;
}> {
  if (!layer) return [];
  const style = readRecord(layer.style);
  const pointcloud = readRecord(style?.pointcloud);
  const pointBudget = readRecord(pointcloud?.pointBudget);
  const chips: Array<{ label: string; icon: IconName; variant: ChipVariant }> = [];

  if (layer.kind === "pointcloud") {
    const pointCount =
      formatCount(pointcloud?.pointCount) ??
      formatCount(pointBudget?.totalPoints);
    chips.push({
      label: pointCount ? `${pointCount} pts` : "Point cloud",
      icon: "blur_on",
      variant: "neutral",
    });
    const density = typeof pointcloud?.densityEstimate === "number"
      ? `${pointcloud.densityEstimate.toFixed(0)} pts/m2`
      : null;
    if (density) chips.push({ label: density, icon: "square_foot", variant: "neutral" });
    if (pointcloud?.hasColor === true) {
      chips.push({ label: "RGB", icon: "palette", variant: "success" });
    } else if (pointcloud?.hasIntensity === true) {
      chips.push({ label: "Intensity", icon: "tune", variant: "info" });
    }
    const budget = formatCount(pointBudget?.recommendedInteractiveBudget);
    if (budget) chips.push({ label: `${budget} budget`, icon: "speed", variant: "neutral" });
    return chips.slice(0, 4);
  }

  if (layer.kind === "3dtiles" || layer.kind === "bim") {
    chips.push({
      label: layer.kind === "3dtiles" ? "3D Tiles" : "BIM / GLB",
      icon: "view_in_ar",
      variant: "neutral",
    });
    const sourceSize = formatBytes(style?.sourceSizeBytes);
    if (sourceSize) chips.push({ label: sourceSize, icon: "database", variant: "neutral" });
    const artifact = typeof style?.gtwinArtifactKind === "string"
      ? style.gtwinArtifactKind
      : null;
    if (artifact) chips.push({ label: artifact, icon: "category", variant: "neutral" });
    chips.push({
      label: layerCoordinateLabel(layer),
      icon: layerCoordinateLabel(layer) === "Local" ? "warning" : "public",
      variant: layerCoordinateLabel(layer) === "Local" ? "warning" : "neutral",
    });
    return chips.slice(0, 4);
  }

  if (layer.kind === "raster") {
    return [{ label: "Raster", icon: "image", variant: "neutral" }];
  }
  if (layer.kind === "terrain") {
    return [{ label: "Terrain", icon: "terrain", variant: "neutral" }];
  }
  if (layer.kind === "vector") {
    const featureCount = formatCount(style?.featureCount);
    if (featureCount) chips.push({ label: `${featureCount} features`, icon: "polyline", variant: "neutral" });
    const geometryType = typeof style?.geometryType === "string" ? style.geometryType : null;
    if (geometryType) chips.push({ label: geometryType, icon: "category", variant: "neutral" });
    const sourceLayer = typeof style?.sourceLayer === "string" ? style.sourceLayer : null;
    if (sourceLayer) chips.push({ label: sourceLayer, icon: "layers", variant: "info" });
    const sourceSize = formatBytes(style?.sourceSizeBytes);
    if (sourceSize) chips.push({ label: sourceSize, icon: "database", variant: "neutral" });
    return chips.length > 0
      ? chips.slice(0, 4)
      : [{ label: "Vector", icon: "polyline", variant: "neutral" }];
  }
  return [{ label: layer.kind, icon: "layers", variant: "neutral" }];
}

function isContainerNode(node: UnifiedSceneTreeNode): boolean {
  return node.kind === "root" || node.kind === "scene" || node.kind === "group";
}

function layerCoordinateLabel(layer: Layer): "Local" | "Projected" {
  const mode = typeof layer.style?.coordinateMode === "string"
    ? layer.style.coordinateMode
    : "";
  return mode === "georeferenced" ? "Projected" : "Local";
}

// ---------------------------------------------------------------------------
// Menu actions (testable in isolation)
// ---------------------------------------------------------------------------

export type SceneTreeMenuActionId =
  | "open-layer-tools"
  | "remove-from-project"
  | "edit-metadata"
  | "save-layer-view"
  | "copy-to-project";

export interface SceneTreeMenuAction {
  readonly id: SceneTreeMenuActionId;
  readonly label: string;
  readonly icon: IconName;
  readonly disabled: boolean;
  readonly disabledReason?: string;
}

/**
 * The four canonical contextual-menu actions for a scene-tree node.
 * Pure helper — exported so tests and future ToolCards can render the
 * same affordance without re-deriving it.
 */
export function buildNodeMenuActions(
  node: UnifiedSceneTreeNode,
  layer: Layer | null,
): readonly SceneTreeMenuAction[] {
  const hasLayer = Boolean(layer);
  return [
    {
      id: "open-layer-tools",
      label: "Open layer tools",
      icon: "tune",
      disabled: !hasLayer && node.kind !== "readiness-gap",
      disabledReason: hasLayer
        ? undefined
        : "This node has no renderable layer to focus.",
    },
    {
      id: "remove-from-project",
      label: "Remove from project",
      icon: "delete",
      disabled: !hasLayer,
      disabledReason: hasLayer
        ? undefined
        : "Only renderable layers can be removed from the project.",
    },
    {
      id: "edit-metadata",
      label: "Edit metadata",
      icon: "edit_note",
      disabled: false,
    },
    {
      id: "save-layer-view",
      label: "Save view of this layer",
      icon: "bookmark",
      disabled: !hasLayer,
      disabledReason: hasLayer
        ? undefined
        : "Saved views require a renderable layer.",
    },
    {
      id: "copy-to-project",
      label: "Copy to another project",
      icon: "content_copy",
      disabled: !hasLayer,
      disabledReason: hasLayer
        ? undefined
        : "Only renderable layers can be copied to another project.",
    },
  ];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SceneTreePanel({
  tree,
  notices,
  layers,
  visibleIds,
  layerOpacity,
  selectedNodeId,
  onSelectNode,
  onSetVisible,
  onSetOpacity,
  onFlyTo,
  onOpenLayerTools,
  onRemoveLayer,
  onEditMetadata,
  onSaveLayerView,
  defaultMenuOpenForNodeId = null,
  defaultShowOnlyVisible = false,
  compact = false,
  onAddLayer,
  layerHealth,
  slotMap,
  onZoomToLayer,
  rasterPaint,
  onSetRasterPaint,
  onSetManyVisible,
  onSoloLayer,
  onMoveLayer,
  onSetPointCloudAppearance,
  onSetPointCloudClassificationFilter,
  onSetVectorStyle,
  onSetVectorLabels,
  compactPresetSwitcher,
  compactSavedViewsControl,
  activePanoramaStationId = null,
  onOpenPanorama,
}: SceneTreePanelProps) {
  const [query, setQuery] = useState("");
  const [collapsedIds, setCollapsedIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [showOnlyVisible, setShowOnlyVisible] = useState<boolean>(
    defaultShowOnlyVisible,
  );
  const [menuOpenForNodeId, setMenuOpenForNodeId] = useState<string | null>(
    defaultMenuOpenForNodeId,
  );
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(
    selectedNodeId ?? null,
  );

  // Keep the focused row in sync when the host selection changes externally.
  useEffect(() => {
    if (selectedNodeId) setFocusedNodeId(selectedNodeId);
  }, [selectedNodeId]);

  const layerById = useMemo(
    () => new Map(layers.map((layer) => [layer.id, layer])),
    [layers],
  );

  const allNodes = useMemo(() => flattenTree(tree), [tree]);
  const allNodeIds = useMemo(
    () => allNodes.map((node) => node.id),
    [allNodes],
  );
  const nodeByLayerId = useMemo(() => {
    const map = new Map<string, UnifiedSceneTreeNode>();
    for (const node of allNodes) {
      for (const layerId of node.sceneItemIds) {
        if (!map.has(layerId)) map.set(layerId, node);
      }
      for (const ref of node.geometryRefs) {
        if (!ref.sceneItemId) continue;
        if (!map.has(ref.sceneItemId)) map.set(ref.sceneItemId, node);
      }
    }
    return map;
  }, [allNodes]);

  // ---- Filter pipeline -----------------------------------------------------
  const queryFiltered = useMemo(
    () => filterSceneTree(tree, query),
    [query, tree],
  );

  const filteredTree = useMemo(() => {
    if (!queryFiltered) return null;
    let result: UnifiedSceneTreeNode | null = queryFiltered;
    if (showOnlyVisible) {
      result = filterTreeByVisibility(result, visibleIds, layerById);
    }
    if (compact && result) {
      result = compactTree(result);
    }
    return result;
  }, [queryFiltered, showOnlyVisible, visibleIds, layerById, compact]);

  const visibleNodeIds = useMemo(() => {
    const ids: string[] = [];
    if (!filteredTree) return ids;
    const visit = (node: UnifiedSceneTreeNode) => {
      ids.push(node.id);
      const expanded = !collapsedIds.has(node.id);
      if (expanded) {
        for (const child of node.children) visit(child);
      }
    };
    visit(filteredTree);
    return ids;
  }, [filteredTree, collapsedIds]);

  const toggleCollapsed = useCallback((id: string) => {
    setCollapsedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => setCollapsedIds(new Set()), []);
  const collapseAll = useCallback(
    () => setCollapsedIds(new Set(allNodeIds)),
    [allNodeIds],
  );

  // ---- Default menu-action handlers (overridable via props) ---------------

  const layerForNode = useCallback(
    (node: UnifiedSceneTreeNode): Layer | null => {
      // Container/group nodes are palette headers, not renderable layers.
      // Some scene-index builders attach aggregated sceneItemIds to group
      // nodes for search/linking, but using those IDs here makes the group
      // row inherit a child layer's metadata and visibility toggle. That is
      // what made "Reality data" hide the 3D model. Only leaf/renderable
      // nodes should expose layer controls.
      if (isContainerNode(node)) return null;
      for (const id of node.sceneItemIds) {
        const layer = layerById.get(id);
        if (layer) return layer;
      }
      for (const ref of node.geometryRefs) {
        if (!ref.sceneItemId) continue;
        const layer = layerById.get(ref.sceneItemId);
        if (layer) return layer;
      }
      return null;
    },
    [layerById],
  );

  const nodeForLayer = useCallback(
    (layer: Layer): UnifiedSceneTreeNode => {
      const existing = nodeByLayerId.get(layer.id);
      if (existing) return existing;
      return {
        id: layer.id,
        label: layer.label ?? layer.id,
        kind: "layer",
        sceneItemIds: [layer.id],
        geometryRefs: [],
        readiness: [],
        integrationStates: [],
        children: [],
      } as unknown as UnifiedSceneTreeNode;
    },
    [nodeByLayerId],
  );

  const defaultOpenLayerTools = useCallback(
    (node: UnifiedSceneTreeNode, layer: Layer | null) => {
      const selection = selectFromTreeNode(node, layer, "scene-tree");
      // Lazy import the store so this module stays SSR-friendly and
      // testable without the global. The store re-exports the helper
      // via `setSceneSelection` and is wired into WP-2.
      void import("@/state/useViewerStore").then(({ useViewerStore }) => {
        useViewerStore.getState().setSceneSelection(selection);
      });
      const detail = dispatchToolCardRequest(selection);
      onOpenLayerTools?.(selection, detail);
      onSelectNode?.(node);
    },
    [onOpenLayerTools, onSelectNode],
  );

  const defaultRemoveLayer = useCallback(
    (node: UnifiedSceneTreeNode, layer: Layer | null) => {
      onRemoveLayer?.(node, layer);
      if (onRemoveLayer || !layer) return;
      // TODO(WP-1): once `gtwinClient.scene.items.remove` lands in
      // `frontend/src/sdk-stub/gtwin-sdk/client.ts`, swap this
      // best-effort fallback for the real SDK call.
      void import("@/app/gtwinClientSingleton").then(({ gtwinClient }) => {
        const sceneAny = gtwinClient.scene as unknown as {
          items?: {
            remove?: (gtwinId: string, itemId: string) => Promise<void>;
          };
        };
        if (typeof sceneAny.items?.remove === "function") {
          // WP-1 SDK surface present — delegate.
          // We do not have a gTwin id here; the host is expected to
          // override `onRemoveLayer` with one. This branch only fires
          // in the developer mock path.
          void sceneAny.items.remove("__mock__", layer.id).catch(() => {
            /* noop — dev mock */
          });
        }
      });
      void import("@/state/useViewerStore").then(({ useViewerStore }) => {
        useViewerStore.getState().removeLayer(layer.id);
      });
    },
    [onRemoveLayer],
  );

  const defaultEditMetadata = useCallback(
    (node: UnifiedSceneTreeNode, layer: Layer | null) => {
      if (onEditMetadata) {
        onEditMetadata(node, layer);
        return;
      }
      // Fallback: route to the existing semantic-property card by
      // selecting the node. WP-7 (Vector ToolCard) will fully wire the
      // metadata editor.
      onSelectNode?.(node);
    },
    [onEditMetadata, onSelectNode],
  );

  const defaultSaveLayerView = useCallback(
    (node: UnifiedSceneTreeNode, layer: Layer | null) => {
      if (onSaveLayerView) {
        onSaveLayerView(node, layer);
        return;
      }
      // Fallback: best-effort save into the local saved-views store
      // until WP-1 finalizes the per-layer scene-preset writer.
      if (!layer) return;
      void import("@/state/useViewerStore").then(({ useViewerStore }) => {
        useViewerStore.getState().saveCurrentView(
          `Layer view — ${layer.label ?? layer.id}`,
        );
      });
    },
    [onSaveLayerView],
  );

  const handleMenuAction = useCallback(
    (
      action: SceneTreeMenuActionId,
      node: UnifiedSceneTreeNode,
      layer: Layer | null,
    ) => {
      switch (action) {
        case "open-layer-tools":
          defaultOpenLayerTools(node, layer);
          break;
        case "remove-from-project":
          defaultRemoveLayer(node, layer);
          break;
        case "edit-metadata":
          defaultEditMetadata(node, layer);
          break;
        case "save-layer-view":
          defaultSaveLayerView(node, layer);
          break;
      }
      setMenuOpenForNodeId(null);
    },
    [
      defaultEditMetadata,
      defaultOpenLayerTools,
      defaultRemoveLayer,
      defaultSaveLayerView,
    ],
  );

  const handleCardBulkVisibility = useCallback(
    (ids: ReadonlyArray<string>, visible: boolean) => {
      if (ids.length === 0) return;
      if (onSetManyVisible) {
        onSetManyVisible(ids, visible);
        return;
      }
      for (const id of ids) onSetVisible(id, visible);
    },
    [onSetManyVisible, onSetVisible],
  );

  const handleCardOpenLayerTools = useCallback(
    (layer: Layer) => {
      const node = nodeForLayer(layer);
      defaultOpenLayerTools(node, layer);
    },
    [defaultOpenLayerTools, nodeForLayer],
  );

  const handleCardEditMetadata = useCallback(
    (layer: Layer) => {
      const node = nodeForLayer(layer);
      defaultEditMetadata(node, layer);
    },
    [defaultEditMetadata, nodeForLayer],
  );

  const handleCardSaveLayerView = useCallback(
    (layer: Layer) => {
      const node = nodeForLayer(layer);
      defaultSaveLayerView(node, layer);
    },
    [defaultSaveLayerView, nodeForLayer],
  );

  const handleCardRemoveLayer = useCallback(
    (layer: Layer) => {
      const node = nodeForLayer(layer);
      defaultRemoveLayer(node, layer);
    },
    [defaultRemoveLayer, nodeForLayer],
  );

  // ---- Roving tabindex / arrow-key navigation -----------------------------

  const treeRef = useRef<HTMLDivElement | null>(null);

  const moveFocus = useCallback(
    (delta: 1 | -1) => {
      if (visibleNodeIds.length === 0) return;
      const currentIdx = focusedNodeId
        ? visibleNodeIds.indexOf(focusedNodeId)
        : -1;
      const nextIdx =
        currentIdx === -1
          ? delta === 1
            ? 0
            : visibleNodeIds.length - 1
          : Math.max(
              0,
              Math.min(visibleNodeIds.length - 1, currentIdx + delta),
            );
      setFocusedNodeId(visibleNodeIds[nextIdx] ?? null);
    },
    [focusedNodeId, visibleNodeIds],
  );

  const onTreeKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        moveFocus(1);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        moveFocus(-1);
        return;
      }
      if (event.key === "Home") {
        event.preventDefault();
        if (visibleNodeIds[0]) setFocusedNodeId(visibleNodeIds[0]);
        return;
      }
      if (event.key === "End") {
        event.preventDefault();
        const last = visibleNodeIds[visibleNodeIds.length - 1];
        if (last) setFocusedNodeId(last);
        return;
      }
      if (event.key === "ArrowRight") {
        if (!focusedNodeId) return;
        if (collapsedIds.has(focusedNodeId)) {
          event.preventDefault();
          toggleCollapsed(focusedNodeId);
        }
        return;
      }
      if (event.key === "ArrowLeft") {
        if (!focusedNodeId) return;
        if (!collapsedIds.has(focusedNodeId)) {
          event.preventDefault();
          toggleCollapsed(focusedNodeId);
        }
        return;
      }
      if (event.key === "Escape" && menuOpenForNodeId) {
        event.preventDefault();
        setMenuOpenForNodeId(null);
        return;
      }
      if (
        (event.key === "F10" && event.shiftKey) ||
        event.key === "ContextMenu"
      ) {
        if (!focusedNodeId) return;
        event.preventDefault();
        setMenuOpenForNodeId(focusedNodeId);
      }
    },
    [
      collapsedIds,
      focusedNodeId,
      menuOpenForNodeId,
      moveFocus,
      toggleCollapsed,
      visibleNodeIds,
    ],
  );

  // Close the menu when the user clicks outside the panel.
  useEffect(() => {
    if (!menuOpenForNodeId) return;
    if (typeof document === "undefined") return;
    const onDocumentClick = (event: MouseEvent) => {
      const root = treeRef.current;
      if (!root) return;
      if (event.target instanceof Node && root.contains(event.target)) return;
      setMenuOpenForNodeId(null);
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [menuOpenForNodeId]);

  // ---- Render --------------------------------------------------------------

  const totalCount = allNodes.length;
  const visibleLayerCount = useMemo(() => {
    let count = 0;
    for (const node of allNodes) {
      const layer = layerForNode(node);
      if (layer && visibleIds.has(layer.id)) count += 1;
    }
    return count;
  }, [allNodes, layerForNode, visibleIds]);

  return (
    <section
      aria-label="Unified scene tree"
      style={{
        padding: tokens.space[3],
        borderBottom: compact ? 0 : tokens.border.thin,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: tokens.space[2],
        }}
      >
        <h2
          style={{
            margin: tokens.space[0],
            fontSize: tokens.font.size.lg,
            fontWeight: tokens.font.weight.semibold,
          }}
        >
          {compact ? "Layers" : "Scene tree"}
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: tokens.space[2] }}>
          <span
            style={{
              padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
              borderRadius: tokens.radius.pill,
              background: tokens.color.surface[2],
              color: tokens.color.ink.secondary,
              fontSize: tokens.font.size.xs,
            }}
            aria-label={compact
              ? `${visibleLayerCount} layer${visibleLayerCount === 1 ? "" : "s"}`
              : `${totalCount} scene-index nodes, ${visibleLayerCount} visible layers`
            }
          >
            {compact
              ? `${visibleLayerCount} layer${visibleLayerCount === 1 ? "" : "s"}`
              : `${totalCount} nodes · ${visibleLayerCount} visible`
            }
          </span>
          {compact && onAddLayer ? (
            <button
              type="button"
              onClick={onAddLayer}
              aria-label="Add layer to project"
              title="Add layer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: tokens.space[5] + tokens.space[1],
                height: tokens.space[5] + tokens.space[1],
                borderRadius: tokens.radius.input,
                border: tokens.border.thin,
                background: tokens.color.accent.primary,
                color: tokens.color.accent.on,
                cursor: "pointer",
                padding: 0,
              }}
            >
              <Icon name="add" size={16} decorative />
            </button>
          ) : null}
          {compact && compactSavedViewsControl ? compactSavedViewsControl : null}
        </div>
      </div>

      {compact && compactPresetSwitcher ? (
        <div
          role="toolbar"
          aria-label="Scene presets"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: tokens.space[1],
            marginTop: tokens.space[2],
          }}
        >
          {compactPresetSwitcher}
        </div>
      ) : null}

      {/* Hide search/filter controls in compact mode for small projects */}
      {!(compact && totalCount <= 5) ? (
        <>
          <label
            style={{
              display: "grid",
              gap: tokens.space[1],
              marginTop: tokens.space[3],
            }}
          >
            <span
              style={{
                color: tokens.color.ink.secondary,
                fontSize: tokens.font.size.xs,
              }}
            >
              Filter layers and objects
            </span>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: tokens.space[2],
                minHeight: tokens.density.touchMin,
                paddingInline: tokens.space[2],
                border: tokens.border.thin,
                borderRadius: tokens.radius.input,
                background: tokens.color.surface[0],
              }}
            >
              <Icon name="search" size={20} decorative />
              <input
                value={query}
                onChange={(event) => setQuery(event.currentTarget.value)}
                placeholder="Search scene-index nodes, feature ids, readiness"
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: 0,
                  outline: 0,
                  background: "transparent",
                  color: tokens.color.ink.primary,
                  fontSize: tokens.font.size.sm,
                }}
              />
            </span>
          </label>

          <div
            role="toolbar"
            aria-label="Scene tree filters"
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: tokens.space[2],
              marginTop: tokens.space[2],
              alignItems: "center",
            }}
          >
            <button
              type="button"
              onClick={expandAll}
              style={treeButtonStyle()}
              aria-label="Expand all scene-tree nodes"
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={collapseAll}
              style={treeButtonStyle()}
              aria-label="Collapse all scene-tree nodes"
            >
              Collapse all
            </button>
            <Chip
              label="Show only visible"
              icon={showOnlyVisible ? "visibility" : "visibility_off"}
              variant={showOnlyVisible ? "info" : "neutral"}
              pressable
              pressed={showOnlyVisible}
              onClick={() => setShowOnlyVisible((value) => !value)}
              aria-label={
                showOnlyVisible
                  ? "Showing only visible layers — click to show all"
                  : "Showing all nodes — click to show only visible layers"
              }
            />
          </div>
        </>
      ) : null}

      {!compact && notices.length > 0 ? (
        <div
          aria-label="Scene readiness"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: tokens.space[1],
            marginTop: tokens.space[2],
          }}
        >
          {notices.slice(0, 6).map((notice) => (
            <Chip
              key={notice.id}
              label={notice.label}
              icon={noticeIcon(notice.severity)}
              variant={noticeVariant(notice.severity)}
              aria-label={`${notice.severity} scene readiness notice: ${notice.label}`}
            />
          ))}
        </div>
      ) : null}

      {compact && slotMap ? (
        <GroupedLayerList
          layers={layers}
          slotMap={slotMap}
          visibleIds={visibleIds}
          layerOpacity={layerOpacity}
          layerHealth={layerHealth}
          rasterPaint={rasterPaint}
          onSetVisible={onSetVisible}
          onSetOpacity={onSetOpacity}
          onZoomToLayer={onZoomToLayer}
          onFlyTo={onFlyTo}
          onRemoveLayer={onRemoveLayer}
          onSetRasterPaint={onSetRasterPaint}
          onSetManyVisible={handleCardBulkVisibility}
          onMoveLayer={onMoveLayer}
          onOpenLayerTools={handleCardOpenLayerTools}
          onEditMetadata={handleCardEditMetadata}
          onSaveLayerView={handleCardSaveLayerView}
          onSoloLayer={onSoloLayer}
          onRemoveLayerFromMenu={handleCardRemoveLayer}
          onSetPointCloudAppearance={onSetPointCloudAppearance}
          onSetPointCloudClassificationFilter={onSetPointCloudClassificationFilter}
          onSetVectorStyle={onSetVectorStyle}
          onSetVectorLabels={onSetVectorLabels}
          onOpenPanorama={onOpenPanorama}
        />
      ) : null}

      {!(compact && slotMap) && filteredTree ? (
        <div
          ref={treeRef}
          role="tree"
          aria-label="Scene index nodes"
          onKeyDown={onTreeKeyDown}
          style={{ marginTop: tokens.space[3] }}
        >
          <TreeNodeView
            node={filteredTree}
            depth={0}
            layerForNode={layerForNode}
            visibleIds={visibleIds}
            layerOpacity={layerOpacity}
            selectedNodeId={selectedNodeId}
            focusedNodeId={focusedNodeId}
            collapsedIds={collapsedIds}
            menuOpenForNodeId={menuOpenForNodeId}
            onToggleCollapsed={toggleCollapsed}
            onSelectNode={onSelectNode}
            onSetVisible={onSetVisible}
            onSetOpacity={onSetOpacity}
            onFlyTo={onFlyTo}
            onFocusNode={setFocusedNodeId}
            onOpenMenu={setMenuOpenForNodeId}
            onMenuAction={handleMenuAction}
            compact={compact}
            activePanoramaStationId={activePanoramaStationId}
          />
        </div>
      ) : !(compact && slotMap) ? (
        <div
          role="status"
          style={{
            marginTop: tokens.space[3],
            padding: tokens.space[3],
            border: tokens.border.thin,
            borderRadius: tokens.radius.md,
            background: tokens.color.surface[0],
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.sm,
          }}
        >
          {showOnlyVisible
            ? "No visible layers match this filter."
            : "No scene-index nodes match this filter."}
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Tree node view
// ---------------------------------------------------------------------------

interface TreeNodeViewProps {
  readonly node: UnifiedSceneTreeNode;
  readonly depth: number;
  readonly layerForNode: (node: UnifiedSceneTreeNode) => Layer | null;
  readonly visibleIds: ReadonlySet<string>;
  readonly layerOpacity: Readonly<Record<string, number>>;
  readonly selectedNodeId?: string | null;
  readonly focusedNodeId: string | null;
  readonly collapsedIds: ReadonlySet<string>;
  readonly menuOpenForNodeId: string | null;
  readonly onToggleCollapsed: (id: string) => void;
  readonly onSelectNode?: (node: UnifiedSceneTreeNode) => void;
  readonly onSetVisible: (id: string, visible: boolean) => void;
  readonly onSetOpacity: (id: string, opacity: number) => void;
  readonly onFlyTo: (layer: Layer) => void;
  readonly onFocusNode: (id: string | null) => void;
  readonly onOpenMenu: (id: string | null) => void;
  readonly onMenuAction: (
    action: SceneTreeMenuActionId,
    node: UnifiedSceneTreeNode,
    layer: Layer | null,
  ) => void;
  readonly compact?: boolean;
  readonly activePanoramaStationId?: string | null;
}

function TreeNodeView({
  node,
  depth,
  layerForNode,
  visibleIds,
  layerOpacity,
  selectedNodeId,
  focusedNodeId,
  collapsedIds,
  menuOpenForNodeId,
  onToggleCollapsed,
  onSelectNode,
  onSetVisible,
  onSetOpacity,
  onFlyTo,
  onFocusNode,
  onOpenMenu,
  onMenuAction,
  compact = false,
  activePanoramaStationId = null,
}: TreeNodeViewProps) {
  const layer = layerForNode(node);
  const visible = layer ? visibleIds.has(layer.id) : false;
  const opacity = layer ? (layerOpacity[layer.id] ?? 1) : 1;
  const stationId = readPanoramaBubbleStationId(node);
  const isActiveStation =
    isPanoramaBubbleStationNode(node) &&
    activePanoramaStationId != null &&
    stationId === activePanoramaStationId;
  const selected = selectedNodeId === node.id || isActiveStation;
  const focused = focusedNodeId === node.id;
  const hasChildren = node.children.length > 0;
  const expanded = hasChildren && !collapsedIds.has(node.id);
  const menuOpen = menuOpenForNodeId === node.id;
  const tabIndex = focused ? 0 : -1;

  const onContextMenu = (event: ReactMouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onFocusNode(node.id);
    onOpenMenu(node.id);
  };

  return (
    <div
      role="treeitem"
      aria-expanded={hasChildren ? expanded : undefined}
      aria-selected={selected}
      tabIndex={tabIndex}
      data-node-id={node.id}
      onContextMenu={onContextMenu}
      onFocus={() => onFocusNode(node.id)}
      style={{
        marginTop: depth === 0 ? 0 : tokens.space[1],
        marginLeft: depth === 0 ? 0 : tokens.space[3],
      }}
    >
      <article
        style={{
          border: tokens.border.thin,
          borderRadius: tokens.radius.md,
          padding: tokens.space[2],
          background: selected
            ? tokens.color.surface[3]
            : depth === 0
              ? tokens.color.surface[2]
              : tokens.color.surface[0],
          cursor: onSelectNode ? "pointer" : "default",
          position: "relative",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: tokens.space[2],
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: tokens.space[1],
                width: "100%",
              }}
            >
              {hasChildren ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggleCollapsed(node.id);
                  }}
                  // WP-11: bumped from `rowDense` (32 px) to `touchMin`
                  // (40 px) per WP-2 supervisor verdict §2.2 Note 4 +
                  // DESIGN_POLICY.md §5 ("40 px minimum touch target").
                  style={{
                    minWidth: tokens.density.touchMin,
                    minHeight: tokens.density.touchMin,
                    border: 0,
                    borderRadius: tokens.radius.input,
                    background: "transparent",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: tokens.color.ink.secondary,
                  }}
                  aria-label={
                    expanded ? `Collapse ${node.label}` : `Expand ${node.label}`
                  }
                >
                  <Icon
                    name={expanded ? "keyboard_arrow_up" : "chevron_right"}
                    size={20}
                    decorative
                  />
                </button>
              ) : (
                // Match the new 40 px chevron footprint so unaffected rows
                // align horizontally; preserves the WP-2 visual rhythm.
                <span style={{ width: tokens.density.touchMin }} />
              )}
              {isPanoramaBubbleNode(node) || isPanoramaBubbleStationNode(node) ? (
                <Icon
                  name={PANORAMA_BUBBLE_NODE_ICON}
                  size={20}
                  decorative
                />
              ) : null}
              <button
                type="button"
                onClick={() => onSelectNode?.(node)}
                style={{
                  minWidth: 0,
                  flex: 1,
                  border: 0,
                  padding: tokens.space[0],
                  background: "transparent",
                  cursor: onSelectNode ? "pointer" : "default",
                  textAlign: "left",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: tokens.color.ink.primary,
                  fontSize: tokens.font.size.sm,
                  fontWeight:
                    depth === 0
                      ? tokens.font.weight.semibold
                      : tokens.font.weight.medium,
                }}
                title={node.label}
              >
                {node.label}
              </button>
              {compact && layer ? (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    onSetVisible(layer.id, !visible);
                  }}
                  aria-label={visible ? `Hide ${node.label}` : `Show ${node.label}`}
                  title={visible ? "Hide layer" : "Show layer"}
                  style={{
                    minWidth: tokens.density.touchMin,
                    minHeight: tokens.density.touchMin,
                    border: 0,
                    borderRadius: tokens.radius.input,
                    background: visible
                      ? tokens.color.accent.subtle
                      : "transparent",
                    cursor: "pointer",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: visible
                      ? tokens.color.accent.primary
                      : tokens.color.ink.muted,
                  }}
                >
                  <Icon
                    name={visible ? "visibility" : "visibility_off"}
                    size={20}
                    decorative
                  />
                </button>
              ) : null}
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onFocusNode(node.id);
                  onOpenMenu(menuOpen ? null : node.id);
                }}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label={`Open contextual menu for ${node.label}`}
                style={{
                  minWidth: tokens.density.touchMin,
                  minHeight: tokens.density.touchMin,
                  border: 0,
                  borderRadius: tokens.radius.input,
                  background: menuOpen
                    ? tokens.color.surface[3]
                    : "transparent",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: tokens.color.ink.secondary,
                }}
              >
                <Icon name="more_vert" size={20} decorative />
              </button>
            </div>
            <div
              style={{
                marginTop: tokens.space[1],
                color: tokens.color.ink.secondary,
                fontSize: tokens.font.size.xs,
              }}
            >
              {nodeKindLabel(node)}
              {node.intelligenceLevel ? ` · ${node.intelligenceLevel}` : ""}
            </div>
            {compact && layerMetricChips(layer).length ? (
              <div
                aria-label={`Layer metrics for ${node.label}`}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: tokens.space[1],
                  marginTop: tokens.space[1],
                }}
              >
                {layerMetricChips(layer).map((chip) => (
                  <Chip
                    key={`${chip.icon}:${chip.label}`}
                    label={chip.label}
                    icon={chip.icon}
                    variant={chip.variant}
                    aria-label={chip.label}
                  />
                ))}
              </div>
            ) : null}
            {compact && layer ? (
              <div
                aria-label={`Quick actions for ${node.label}`}
                style={{
                  display: "grid",
                  gap: tokens.space[1],
                  marginTop: tokens.space[2],
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: tokens.space[1],
                  }}
                >
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onFlyTo(layer);
                    }}
                    style={compactActionButtonStyle()}
                  >
                    <Icon name="my_location" size={16} decorative />
                    Zoom
                  </button>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onSelectNode?.(node);
                    }}
                    style={compactActionButtonStyle()}
                  >
                    <Icon name="tune" size={16} decorative />
                    Tools
                  </button>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onSetVisible(layer.id, !visible);
                    }}
                    style={compactActionButtonStyle()}
                  >
                    <Icon name={visible ? "visibility_off" : "visibility"} size={16} decorative />
                    {visible ? "Hide" : "Show"}
                  </button>
                </div>
                <label
                  onClick={(event) => event.stopPropagation()}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto",
                    alignItems: "center",
                    gap: tokens.space[1],
                    color: tokens.color.ink.secondary,
                    fontSize: tokens.font.size.xs,
                  }}
                >
                  <span>Opacity</span>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={Math.round(opacity * 100)}
                    onChange={(event) => {
                      onSetOpacity(layer.id, Number(event.currentTarget.value) / 100);
                    }}
                    style={{ width: "100%" }}
                    aria-label={`Set opacity for ${node.label}`}
                  />
                  <span>{Math.round(opacity * 100)}%</span>
                </label>
              </div>
            ) : null}
            {!compact && node.readiness.length ? (
              <div
                aria-label={`Readiness states for ${node.label}`}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: tokens.space[1],
                  marginTop: tokens.space[1],
                }}
              >
                {node.readiness.map((state) => {
                  const appearance = readinessAppearance(state);
                  return (
                    <Chip
                      key={state}
                      label={appearance.label}
                      icon={appearance.icon}
                      variant={appearance.variant}
                      aria-label={`Readiness: ${state}`}
                    />
                  );
                })}
              </div>
            ) : null}
            {node.integrationStates.length ? (
              <div
                aria-label={`Operational integration states for ${node.label}`}
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: tokens.space[1],
                  marginTop: tokens.space[1],
                }}
              >
                {node.integrationStates.map((state) => (
                  <Chip
                    key={`${state.state}:${state.providerType ?? ""}:${state.status ?? ""}:${state.permissionState ?? ""}`}
                    label={state.label}
                    icon={integrationStateIcon(state)}
                    variant={integrationStateVariant(state)}
                    aria-label={`${state.label} integration state`}
                  />
                ))}
              </div>
            ) : null}
            {!compact && rasterAnalysisSummary(node) ? (
              <div
                style={{
                  marginTop: tokens.space[1],
                  color: tokens.color.ink.secondary,
                  fontSize: tokens.font.size.xs,
                }}
              >
                {rasterAnalysisSummary(node)}
              </div>
            ) : null}
          </div>
          {!compact && layer ? (
            <div
              style={{
                display: "flex",
                gap: tokens.space[1],
                alignItems: "flex-start",
              }}
            >
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onSetVisible(layer.id, !visible);
                }}
                style={treeButtonStyle()}
              >
                {visible ? "Hide" : "Show"}
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onFlyTo(layer);
                }}
                style={treeButtonStyle()}
              >
                Fly to
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onSelectNode?.(node);
                }}
                style={treeButtonStyle()}
              >
                Details
              </button>
            </div>
          ) : null}
        </div>
        {!compact && layer ? (
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.space[2],
              marginTop: tokens.space[2],
              color: tokens.color.ink.secondary,
              fontSize: tokens.font.size.xs,
            }}
          >
            {/*
             * WP-11: re-token the opacity-row widths per WP-2 supervisor
             * verdict §2.2 Note 5. The leading "Opacity" label uses
             * `space[7]` (48 px) as its column floor, the trailing
             * percentage readout uses `space[6]` (32 px); both line up
             * with the surrounding `space[2]` gaps so the row stays
             * scannable in a dense rail.
             */}
            <span style={{ minWidth: tokens.space[7] }}>Opacity</span>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={opacity}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) =>
                onSetOpacity(layer.id, Number(event.currentTarget.value))
              }
              style={{ flex: 1 }}
              aria-label={`Opacity for tree layer ${layer.label ?? layer.id}`}
            />
            <span
              style={{
                minWidth: tokens.space[6],
                textAlign: "right",
                fontFamily: tokens.font.family.mono,
              }}
            >
              {Math.round(opacity * 100)}%
            </span>
          </label>
        ) : null}
        {!compact ? (
        <div
          style={{
            display: "flex",
            gap: tokens.space[1],
            flexWrap: "wrap",
            marginTop: tokens.space[2],
          }}
        >
          <button
            type="button"
            onClick={() => copyText(node.id)}
            style={treeButtonStyle()}
          >
            Copy id
          </button>
          {layer ? (
            <button
              type="button"
              onClick={() => copyText(firstEndpoint(layer) ?? layer.id)}
              style={treeButtonStyle()}
            >
              Show source
            </button>
          ) : null}
        </div>
        ) : null}
        {menuOpen ? (
          <NodeContextMenu
            node={node}
            layer={layer}
            onAction={onMenuAction}
            onClose={() => onOpenMenu(null)}
          />
        ) : null}
      </article>
      {expanded ? (
        <div role="group">
          {node.children.map((child) => (
            <TreeNodeView
              key={child.id}
              node={child}
              depth={depth + 1}
              layerForNode={layerForNode}
              visibleIds={visibleIds}
              layerOpacity={layerOpacity}
              selectedNodeId={selectedNodeId}
              focusedNodeId={focusedNodeId}
              collapsedIds={collapsedIds}
              menuOpenForNodeId={menuOpenForNodeId}
              onToggleCollapsed={onToggleCollapsed}
              onSelectNode={onSelectNode}
              onSetVisible={onSetVisible}
              onSetOpacity={onSetOpacity}
              onFlyTo={onFlyTo}
              onFocusNode={onFocusNode}
              onOpenMenu={onOpenMenu}
              onMenuAction={onMenuAction}
              compact={compact}
              activePanoramaStationId={activePanoramaStationId}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contextual menu (exported so tests can render it standalone)
// ---------------------------------------------------------------------------

export interface NodeContextMenuProps {
  readonly node: UnifiedSceneTreeNode;
  readonly layer: Layer | null;
  readonly onAction: (
    action: SceneTreeMenuActionId,
    node: UnifiedSceneTreeNode,
    layer: Layer | null,
  ) => void;
  readonly onClose?: () => void;
}

export function NodeContextMenu({
  node,
  layer,
  onAction,
  onClose,
}: NodeContextMenuProps) {
  const actions = buildNodeMenuActions(node, layer);
  // WP-11: collapse the per-row inline `<button role="menuitem">` shape
  // into the shared `<Menu />` primitive so the contextual menu inherits
  // the full WAI-ARIA roving-tabindex / arrow-key / Home-End / Escape /
  // type-ahead model. The container width is now the dedicated
  // `tokens.layout.popoverXs` (240 px) — closes WP-2 supervisor verdict
  // §2.2 Note 1 and Note 3 in one place.
  const items: readonly MenuItemDef[] = actions.map((action) => ({
    id: action.id,
    label: action.label,
    icon: action.icon,
    disabled: action.disabled,
    disabledReason: action.disabledReason,
  }));
  return (
    <Menu
      aria-label={`Layer actions for ${node.label}`}
      items={items}
      onSelect={(id) => onAction(id as SceneTreeMenuActionId, node, layer)}
      onClose={onClose}
      style={{
        position: "absolute",
        right: tokens.space[2],
        top: `calc(100% + ${tokens.space[1]}px)`,
        minWidth: tokens.layout.popoverXs,
        padding: tokens.space[1],
        borderRadius: tokens.radius.md,
        border: tokens.border.thin,
        background: tokens.color.surface[1],
        boxShadow: tokens.elevation[2],
        zIndex: tokens.zIndex.tooltip,
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function flattenTree(node: UnifiedSceneTreeNode): UnifiedSceneTreeNode[] {
  const out: UnifiedSceneTreeNode[] = [];
  const visit = (current: UnifiedSceneTreeNode) => {
    out.push(current);
    for (const child of current.children) visit(child);
  };
  visit(node);
  return out;
}

/**
 * Compact-mode tree simplification. Skips container-only nodes (root,
 * scene, group) that are just wrappers with a single child chain, and
 * promotes leaf layers to the top level. The result is a flat list of
 * the actual data layers the user cares about, not the internal
 * scene-index graph hierarchy.
 */
function compactTree(node: UnifiedSceneTreeNode): UnifiedSceneTreeNode {
  const containerKinds = new Set(["root", "scene", "group"]);
  // Walk down single-child container chains to find meaningful content.
  let current = node;
  while (
    containerKinds.has(current.kind) &&
    current.children.length === 1 &&
    current.sceneItemIds.length === 0 &&
    current.geometryRefs.length === 0
  ) {
    current = current.children[0];
  }
  // If the unwrapped node is still a container with children, recursively
  // compact each child.
  if (current.children.length > 0) {
    const compacted = current.children.map(compactTree);
    // If compacting left us with a single-child container, unwrap again.
    if (compacted.length === 1 && containerKinds.has(current.kind)) {
      return compacted[0];
    }
    return { ...current, children: compacted };
  }
  return current;
}

function filterTreeByVisibility(
  tree: UnifiedSceneTreeNode,
  visibleIds: ReadonlySet<string>,
  layerById: ReadonlyMap<string, Layer>,
): UnifiedSceneTreeNode | null {
  const visit = (
    node: UnifiedSceneTreeNode,
  ): UnifiedSceneTreeNode | null => {
    const children = node.children
      .map(visit)
      .filter((child): child is UnifiedSceneTreeNode => Boolean(child));
    const ownLayers = collectNodeLayerIds(node);
    const isVisibleLayer = ownLayers.some(
      (id) => layerById.has(id) && visibleIds.has(id),
    );
    if (isVisibleLayer || children.length > 0) {
      return { ...node, children };
    }
    return null;
  };
  return visit(tree);
}

function collectNodeLayerIds(node: UnifiedSceneTreeNode): string[] {
  const ids = new Set<string>(node.sceneItemIds);
  for (const ref of node.geometryRefs) {
    if (ref.sceneItemId) ids.add(ref.sceneItemId);
  }
  return [...ids];
}

function copyText(value: string): void {
  if (typeof navigator === "undefined") return;
  void navigator.clipboard?.writeText(value);
}

function firstEndpoint(layer: Layer): string | null {
  return (
    Object.values(layer.endpoints).find(
      (value): value is string => typeof value === "string",
    ) ?? null
  );
}

/**
 * Human-readable subtitle for a tree node. Uses the layer kind (via
 * semanticKindLabel) when the node has an associated layer, otherwise
 * maps known index node kinds to descriptive labels so users never see
 * raw kind codes like "scene · L1" or "layer · L0".
 */
function nodeKindLabel(node: UnifiedSceneTreeNode): string {
  // If the node has direct scene item references, prefer the semantic kind
  // from the first ref that maps to a known layer family.
  if (node.sceneItemIds.length > 0 || node.geometryRefs.length > 0) {
    // The node.kind can be a layer kind like "pointcloud", "raster", etc.
    const semantic = semanticKindLabel(node.kind);
    if (semantic !== node.kind) return semantic;
  }
  // Map index node kinds to readable labels.
  switch (node.kind) {
    case "scene":
      return "Scene";
    case "group":
      return "Group";
    case "layer":
      return "Layer";
    case "feature":
      return "Feature";
    case "evidence":
      return "Evidence";
    case "integration":
      return "Integration";
    case "readiness-gap":
      return "Readiness gap";
    case "ai-candidate":
      return "AI candidate";
    case "root":
      return "Scene root";
    case "panorama-bubble":
      return "Panoramas (Bubble View)";
    case "panorama-bubble-station":
      return "Scan station";
    default:
      return semanticKindLabel(node.kind);
  }
}

function treeButtonStyle(): CSSProperties {
  return {
    minHeight: tokens.density.touchMin,
    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[1],
    color: tokens.color.ink.primary,
    cursor: "pointer",
    fontSize: tokens.font.size.xs,
  };
}

function compactActionButtonStyle(): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.space[1],
    minHeight: 28,
    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[2],
    color: tokens.color.ink.secondary,
    cursor: "pointer",
    fontSize: tokens.font.size.xs,
  };
}

function noticeVariant(severity: SceneReadinessNotice["severity"]): ChipVariant {
  if (severity === "blocking") return "error";
  if (severity === "warning") return "warning";
  return "info";
}

function noticeIcon(severity: SceneReadinessNotice["severity"]): IconName {
  if (severity === "blocking") return "error";
  if (severity === "warning") return "warning";
  return "info";
}

function integrationStateVariant(
  state: IntegrationOperationalState,
): ChipVariant {
  if (state.state === "permission-denied" || state.state === "conflict")
    return "error";
  if (state.state === "offline" || state.state === "stale") return "warning";
  return "info";
}

function integrationStateIcon(state: IntegrationOperationalState): IconName {
  switch (state.state) {
    case "loading":
      return "pending";
    case "stale":
      return "sync_problem";
    case "offline":
      return "cloud_off";
    case "permission-denied":
      return "lock";
    case "conflict":
      return "warning";
  }
}

function rasterAnalysisSummary(node: UnifiedSceneTreeNode): string | null {
  const analysis = node.rasterTerrainAnalysis;
  if (!analysis) return null;
  const terrainType =
    typeof analysis.terrainType === "string" ? analysis.terrainType : undefined;
  const verticalUnits =
    typeof analysis.verticalUnits === "string"
      ? analysis.verticalUnits
      : undefined;
  const verticalDatum =
    typeof analysis.verticalDatum === "string"
      ? analysis.verticalDatum
      : undefined;
  const bandCount = Array.isArray(analysis.bands)
    ? analysis.bands.length
    : undefined;
  const flags = Array.isArray(analysis.qualityFlags)
    ? analysis.qualityFlags
        .filter((flag): flag is string => typeof flag === "string")
        .slice(0, 2)
    : [];
  const parts = [
    bandCount ? `${bandCount} band${bandCount === 1 ? "" : "s"}` : undefined,
    terrainType,
    verticalUnits,
    verticalDatum,
    ...flags,
  ].filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(" · ") : null;
}

// ---------------------------------------------------------------------------
// Wave-1 — GroupedLayerList (compact mode only)
// ---------------------------------------------------------------------------

const GROUP_EXPANDED_STORAGE_KEY = "geoviewer.layerGroups.expanded.v1";

function readExpandedGroups(): Set<SceneSlotName> {
  if (typeof window === "undefined") {
    return new Set(SLOT_ORDER);
  }
  try {
    const raw = window.localStorage.getItem(GROUP_EXPANDED_STORAGE_KEY);
    if (!raw) return new Set(SLOT_ORDER);
    const parsed = JSON.parse(raw) as string[];
    return new Set(parsed.filter(isSceneSlot));
  } catch {
    return new Set(SLOT_ORDER);
  }
}

function writeExpandedGroups(set: ReadonlySet<SceneSlotName>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      GROUP_EXPANDED_STORAGE_KEY,
      JSON.stringify([...set]),
    );
  } catch {
    /* localStorage may be unavailable (Safari private mode etc.) — ignore */
  }
}

interface GroupedLayerListProps {
  readonly layers: readonly Layer[];
  readonly slotMap: Readonly<Record<string, string>>;
  readonly visibleIds: ReadonlySet<string>;
  readonly layerOpacity: Readonly<Record<string, number>>;
  readonly layerHealth?: Readonly<
    Record<string, "ready" | "loading" | "failed" | "unsupported" | "deferred">
  >;
  readonly rasterPaint?: Readonly<Record<string, Record<string, number>>>;
  readonly onSetVisible: (id: string, visible: boolean) => void;
  readonly onSetOpacity: (id: string, opacity: number) => void;
  readonly onZoomToLayer?: (layerId: string) => void;
  readonly onFlyTo: (layer: Layer) => void;
  readonly onRemoveLayer?: (
    node: UnifiedSceneTreeNode,
    layer: Layer | null,
  ) => void;
  readonly onSetRasterPaint?: (
    layerId: string,
    prop: string,
    value: number,
  ) => void;
  readonly onSetManyVisible?: (
    ids: ReadonlyArray<string>,
    visible: boolean,
  ) => void;
  readonly onMoveLayer?: (id: string, direction: -1 | 1) => void;
  readonly onOpenLayerTools?: (layer: Layer) => void;
  readonly onEditMetadata?: (layer: Layer) => void;
  readonly onSaveLayerView?: (layer: Layer) => void;
  readonly onSoloLayer?: (layerId: string) => void;
  readonly onRemoveLayerFromMenu?: (layer: Layer) => void;
  readonly onSetPointCloudAppearance?: (
    layerId: string,
    patch: {
      pointBudget?: number;
      pointSize?: number;
      colorMode?: "auto" | "rgb" | "intensity" | "classification" | "elevation";
    },
  ) => void;
  readonly onSetPointCloudClassificationFilter?: (
    layerId: string,
    classes: ReadonlySet<number>,
  ) => void;
  readonly onSetVectorStyle?: (
    layerId: string,
    delta: {
      fillColor?: string;
      fillOpacity?: number;
      lineColor?: string;
      lineWidth?: number;
    },
  ) => void;
  readonly onSetVectorLabels?: (
    layerId: string,
    options: { enabled: boolean; field: string | null },
  ) => void;
  readonly onOpenPanorama?: (layer: Layer) => void;
}

function GroupedLayerList(props: GroupedLayerListProps) {
  const { layers, slotMap } = props;
  const [expanded, setExpanded] = useState<Set<SceneSlotName>>(() =>
    readExpandedGroups(),
  );
  const [search, setSearch] = useState("");
  const [orderedLayerIds, setOrderedLayerIds] = useState<string[]>(() =>
    layers.map((layer) => layer.id),
  );

  useEffect(() => {
    setOrderedLayerIds((prev) => {
      const known = new Set(layers.map((layer) => layer.id));
      const retained = prev.filter((id) => known.has(id));
      const added = layers
        .map((layer) => layer.id)
        .filter((id) => !retained.includes(id));
      const next = [...retained, ...added];
      if (
        next.length === prev.length
        && next.every((id, index) => prev[index] === id)
      ) {
        return prev;
      }
      return next;
    });
  }, [layers]);

  const toggleGroup = useCallback((slot: SceneSlotName) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slot)) next.delete(slot);
      else next.add(slot);
      writeExpandedGroups(next);
      return next;
    });
  }, []);

  const grouped = useMemo(() => {
    const normalizedQuery = search.trim().toLowerCase();
    const order = new Map(
      orderedLayerIds.map((id, index) => [id, index]),
    );
    const sortedLayers = [...layers].sort(
      (a, b) => (order.get(a.id) ?? Number.MAX_SAFE_INTEGER)
        - (order.get(b.id) ?? Number.MAX_SAFE_INTEGER),
    );
    const map = new Map<SceneSlotName, Layer[]>();
    for (const slot of SLOT_ORDER) map.set(slot, []);
    for (const layer of sortedLayers) {
      if (normalizedQuery.length > 0) {
        const label = (layer.label ?? layer.id).toLowerCase();
        if (!label.includes(normalizedQuery)) continue;
      }
      const slot = slotMap[layer.id];
      if (isSceneSlot(slot)) {
        map.get(slot)!.push(layer);
      }
    }
    return map;
  }, [layers, orderedLayerIds, search, slotMap]);

  const filteredLayerIds = useMemo(() => {
    const ids: string[] = [];
    for (const slot of SLOT_ORDER) {
      const slotLayers = grouped.get(slot) ?? [];
      for (const layer of slotLayers) ids.push(layer.id);
    }
    return ids;
  }, [grouped]);

  const applyBulkVisibility = useCallback(
    (ids: ReadonlyArray<string>, visible: boolean) => {
      if (ids.length === 0) return;
      if (props.onSetManyVisible) {
        props.onSetManyVisible(ids, visible);
        return;
      }
      for (const id of ids) props.onSetVisible(id, visible);
    },
    [props],
  );

  const moveLayerInOrder = useCallback(
    (slotLayers: readonly Layer[], layerId: string, direction: -1 | 1) => {
      const index = slotLayers.findIndex((candidate) => candidate.id === layerId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= slotLayers.length) return;
      const targetId = slotLayers[target]?.id;
      if (!targetId) return;
      setOrderedLayerIds((prev) => {
        const from = prev.indexOf(layerId);
        const to = prev.indexOf(targetId);
        if (from < 0 || to < 0) return prev;
        const next = [...prev];
        [next[from], next[to]] = [next[to], next[from]];
        return next;
      });
      props.onMoveLayer?.(layerId, direction);
    },
    [props],
  );

  const sectionStyle: CSSProperties = {
    border: tokens.border.thin,
    borderRadius: tokens.radius.md,
    background: tokens.color.surface[1],
    marginBottom: tokens.space[2],
    overflow: "hidden",
  };
  const headerStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: tokens.space[2],
    padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
    background: tokens.color.surface[2],
    borderBottom: tokens.border.thin,
    color: tokens.color.ink.primary,
    fontSize: tokens.font.size.sm,
    fontWeight: tokens.font.weight.medium,
    textAlign: "left",
  };

  return (
    <section
      aria-label="Layers grouped by scene slot"
      style={{ marginTop: tokens.space[3] }}
      data-test-id="grouped-layer-list"
    >
      <label
        style={{
          display: "grid",
          gap: tokens.space[1],
          marginBottom: tokens.space[2],
        }}
      >
        <span
          style={{
            color: tokens.color.ink.secondary,
            fontSize: tokens.font.size.xs,
          }}
        >
          Search layers
        </span>
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: tokens.space[2],
            minHeight: tokens.density.touchMin,
            paddingInline: tokens.space[2],
            border: tokens.border.thin,
            borderRadius: tokens.radius.input,
            background: tokens.color.surface[0],
          }}
        >
          <Icon name="search" size={16} decorative />
          <input
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder="Filter by layer name"
            aria-label="Search grouped layer cards"
            style={{
              flex: 1,
              minWidth: 0,
              border: 0,
              outline: 0,
              background: "transparent",
              color: tokens.color.ink.primary,
              fontSize: tokens.font.size.sm,
            }}
          />
        </span>
      </label>

      <div
        role="toolbar"
        aria-label="Global layer visibility actions"
        style={{
          display: "flex",
          gap: tokens.space[1],
          marginBottom: tokens.space[2],
        }}
      >
        <button
          type="button"
          onClick={() => applyBulkVisibility(filteredLayerIds, true)}
          aria-label="Show all filtered layers"
          style={chipBtnStyle()}
        >
          Show all
        </button>
        <button
          type="button"
          onClick={() => applyBulkVisibility(filteredLayerIds, false)}
          aria-label="Hide all filtered layers"
          style={chipBtnStyle()}
        >
          Hide all
        </button>
      </div>

      {SLOT_ORDER.map((slot) => {
        const slotLayers = grouped.get(slot) ?? [];
        if (slotLayers.length === 0) return null;
        const isExpanded = expanded.has(slot);
        const visibleCount = slotLayers.filter((l) =>
          props.visibleIds.has(l.id),
        ).length;
        const slotLayerIds = slotLayers.map((layer) => layer.id);
        return (
          <div key={slot} style={sectionStyle}>
            <div style={headerStyle}>
              <button
                type="button"
                onClick={() => toggleGroup(slot)}
                aria-expanded={isExpanded}
                aria-controls={`layer-group-${slot}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: tokens.space[2],
                  flex: 1,
                  border: 0,
                  background: "transparent",
                  color: tokens.color.ink.primary,
                  cursor: "pointer",
                  textAlign: "left",
                  minHeight: tokens.density.touchMin,
                }}
              >
                <Icon
                  name={isExpanded ? "keyboard_arrow_up" : "chevron_right"}
                  size={16}
                  decorative
                />
                <span style={{ flex: 1 }}>{SLOT_LABEL[slot]}</span>
                <span
                  style={{
                    color: tokens.color.ink.secondary,
                    fontSize: tokens.font.size.xs,
                    fontWeight: tokens.font.weight.regular,
                  }}
                >
                  {visibleCount}/{slotLayers.length}
                </span>
              </button>
              <button
                type="button"
                onClick={() => applyBulkVisibility(slotLayerIds, true)}
                aria-label={`Show all ${SLOT_LABEL[slot]} layers`}
                style={{
                  background: "transparent",
                  border: tokens.border.thin,
                  borderRadius: tokens.radius.sm,
                  padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                  color: tokens.color.ink.secondary,
                  fontSize: tokens.font.size.xs,
                  cursor: "pointer",
                }}
              >
                Show all
              </button>
              {visibleCount > 0 ? (
                <button
                  type="button"
                  onClick={() => applyBulkVisibility(slotLayerIds, false)}
                  aria-label={`Hide all ${SLOT_LABEL[slot]} layers`}
                  style={{
                    background: "transparent",
                    border: tokens.border.thin,
                    borderRadius: tokens.radius.sm,
                    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                    color: tokens.color.ink.secondary,
                    fontSize: tokens.font.size.xs,
                    cursor: "pointer",
                  }}
                >
                  Hide all
                </button>
              ) : null}
            </div>
            {isExpanded ? (
              <div
                id={`layer-group-${slot}`}
                role="group"
                aria-label={`${SLOT_LABEL[slot]} layers`}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: tokens.space[1],
                  padding: tokens.space[2],
                }}
              >
                {slotLayers.map((layer, index) => (
                  <Wave1LayerCard
                    key={layer.id}
                    layer={layer}
                    visible={props.visibleIds.has(layer.id)}
                    opacity={props.layerOpacity[layer.id] ?? 1}
                    health={props.layerHealth?.[layer.id] ?? "loading"}
                    rasterPaint={props.rasterPaint?.[layer.id] ?? {}}
                    onSetVisible={props.onSetVisible}
                    onSetOpacity={props.onSetOpacity}
                    onZoomToLayer={props.onZoomToLayer}
                    onFlyTo={props.onFlyTo}
                    onRemoveLayer={props.onRemoveLayer}
                    onSetRasterPaint={props.onSetRasterPaint}
                    onOpenLayerTools={props.onOpenLayerTools}
                    onEditMetadata={props.onEditMetadata}
                    onSaveLayerView={props.onSaveLayerView}
                    onSoloLayer={props.onSoloLayer}
                    onRemoveLayerFromMenu={props.onRemoveLayerFromMenu}
                    canMoveUp={index > 0}
                    canMoveDown={index < slotLayers.length - 1}
                    onMoveLayer={props.onMoveLayer
                      ? (layerId, direction) =>
                          moveLayerInOrder(slotLayers, layerId, direction)
                      : undefined}
                    onSetPointCloudAppearance={props.onSetPointCloudAppearance}
                    onSetPointCloudClassificationFilter={props.onSetPointCloudClassificationFilter}
                    onSetVectorStyle={props.onSetVectorStyle}
                    onSetVectorLabels={props.onSetVectorLabels}
                    onOpenPanorama={props.onOpenPanorama}
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
    </section>
  );
}

interface Wave1LayerCardProps {
  readonly layer: Layer;
  readonly visible: boolean;
  readonly opacity: number;
  readonly health: "ready" | "loading" | "failed" | "unsupported" | "deferred";
  readonly rasterPaint: Readonly<Record<string, number>>;
  readonly onSetVisible: (id: string, visible: boolean) => void;
  readonly onSetOpacity: (id: string, opacity: number) => void;
  readonly onZoomToLayer?: (layerId: string) => void;
  readonly onFlyTo: (layer: Layer) => void;
  readonly onRemoveLayer?: (
    node: UnifiedSceneTreeNode,
    layer: Layer | null,
  ) => void;
  readonly onSetRasterPaint?: (
    layerId: string,
    prop: string,
    value: number,
  ) => void;
  readonly onOpenLayerTools?: (layer: Layer) => void;
  readonly onEditMetadata?: (layer: Layer) => void;
  readonly onSaveLayerView?: (layer: Layer) => void;
  readonly onSoloLayer?: (layerId: string) => void;
  readonly onRemoveLayerFromMenu?: (layer: Layer) => void;
  readonly canMoveUp?: boolean;
  readonly canMoveDown?: boolean;
  readonly onMoveLayer?: (layerId: string, direction: -1 | 1) => void;
  readonly onSetPointCloudAppearance?: (
    layerId: string,
    patch: {
      pointBudget?: number;
      pointSize?: number;
      colorMode?: "auto" | "rgb" | "intensity" | "classification" | "elevation";
    },
  ) => void;
  readonly onSetPointCloudClassificationFilter?: (
    layerId: string,
    classes: ReadonlySet<number>,
  ) => void;
  readonly onSetVectorStyle?: (
    layerId: string,
    delta: {
      fillColor?: string;
      fillOpacity?: number;
      lineColor?: string;
      lineWidth?: number;
    },
  ) => void;
  readonly onSetVectorLabels?: (
    layerId: string,
    options: { enabled: boolean; field: string | null },
  ) => void;
  readonly onOpenPanorama?: (layer: Layer) => void;
}

const POINT_CLOUD_COLOR_MODES: ReadonlyArray<{
  id: "auto" | "rgb" | "intensity" | "classification" | "elevation";
  label: string;
}> = [
  { id: "auto", label: "Auto" },
  { id: "rgb", label: "RGB" },
  { id: "intensity", label: "Intensity" },
  { id: "classification", label: "Class" },
  { id: "elevation", label: "Elevation" },
];

const ASPRS_CLASS_LABELS: ReadonlyArray<{ id: number; label: string }> = [
  { id: 0, label: "Created" },
  { id: 1, label: "Unclass." },
  { id: 2, label: "Ground" },
  { id: 3, label: "Low veg" },
  { id: 4, label: "Med veg" },
  { id: 5, label: "High veg" },
  { id: 6, label: "Building" },
  { id: 7, label: "Noise" },
  { id: 8, label: "Model key" },
  { id: 9, label: "Water" },
  { id: 10, label: "Rail" },
  { id: 11, label: "Road" },
  { id: 12, label: "Overlap" },
  { id: 13, label: "Wire guard" },
  { id: 14, label: "Wire cond." },
  { id: 15, label: "Tower" },
];

const VECTOR_COLOR_SWATCHES: ReadonlyArray<{
  id: string;
  label: string;
  value: string;
}> = [
  { id: "accent", label: "Accent", value: tokens.color.accent.primary },
  { id: "info", label: "Info", value: tokens.color.semantic.info },
  { id: "success", label: "Success", value: tokens.color.semantic.success },
  { id: "warning", label: "Warning", value: tokens.color.semantic.warning },
];

function Wave1LayerCard({
  layer,
  visible,
  opacity,
  health,
  rasterPaint,
  onSetVisible,
  onSetOpacity,
  onZoomToLayer,
  onFlyTo,
  onRemoveLayer,
  onSetRasterPaint,
  onOpenLayerTools,
  onEditMetadata,
  onSaveLayerView,
  onSoloLayer,
  onRemoveLayerFromMenu,
  canMoveUp = false,
  canMoveDown = false,
  onMoveLayer,
  onSetPointCloudAppearance,
  onSetPointCloudClassificationFilter,
  onSetVectorStyle,
  onSetVectorLabels,
  onOpenPanorama,
}: Wave1LayerCardProps) {
  const isRaster = layer.kind === "raster" || layer.kind === "terrain";
  const isPointCloud = layer.kind === "pointcloud";
  const isVector = layer.kind === "vector";
  const isPanoramaBubble = layer.kind === "panorama-bubble";
  const unsupportedReason =
    typeof layer.style?.viewerUnsupportedReason === "string"
      ? layer.style.viewerUnsupportedReason
      : undefined;
  const isBasemap = (layer.style?.["family"] ?? "") === "basemap";
  const [imageAdjustOpen, setImageAdjustOpen] = useState<boolean>(
    Boolean(isBasemap),
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [pointAppearanceOpen, setPointAppearanceOpen] = useState(false);
  const [pointClassificationOpen, setPointClassificationOpen] = useState(false);
  const [vectorStyleOpen, setVectorStyleOpen] = useState(false);
  const [vectorLabelsOpen, setVectorLabelsOpen] = useState(false);
  const pointcloudStyle = readRecord(readRecord(layer.style)?.pointcloud);
  const [pointBudgetMillions, setPointBudgetMillions] = useState<number>(() => {
    const raw = pointcloudStyle?.pointBudget;
    if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) {
      return Math.max(1, Math.min(100, Math.round(raw / 1_000_000)));
    }
    return 20;
  });
  const [pointSize, setPointSize] = useState<number>(() => {
    const raw = pointcloudStyle?.pointSize;
    if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) {
      return Math.max(0.5, Math.min(5, raw));
    }
    return 1.5;
  });
  const [pointColorMode, setPointColorMode] = useState<
    "auto" | "rgb" | "intensity" | "classification" | "elevation"
  >("auto");
  const [selectedClasses, setSelectedClasses] = useState<Set<number>>(
    () => new Set(),
  );
  const [lineColor, setLineColor] = useState(VECTOR_COLOR_SWATCHES[0]?.value ?? tokens.color.accent.primary);
  const [fillColor, setFillColor] = useState(VECTOR_COLOR_SWATCHES[1]?.value ?? tokens.color.semantic.info);
  const [lineWidth, setLineWidth] = useState(2);
  const [fillOpacity, setFillOpacity] = useState(0.2);
  const vectorFields = useMemo(() => {
    const fields = readRecord(layer.style)?.fields;
    if (!Array.isArray(fields)) return [] as string[];
    return fields.filter((value): value is string => typeof value === "string");
  }, [layer.style]);
  const [labelsEnabled, setLabelsEnabled] = useState(false);
  const [labelField, setLabelField] = useState<string>(
    vectorFields[0] ?? "",
  );
  const chips = metadataChipsFor(layer);

  // LOD badge — extract numeric LOD from the layer label (e.g. "Buildings LOD2 (Textured)")
  // so the left-rail card can show "LOD2" prominently even when the label gets truncated.
  // Falls back to `null` for layers that don't expose an LOD in their label.
  const lodLevel = useMemo(() => {
    const source = layer.label ?? layer.id ?? "";
    const match = source.match(/\bLOD\s*(\d)\b/i);
    return match ? Number(match[1]) : null;
  }, [layer.label, layer.id]);

  const cardStyle: CSSProperties = {
    border: tokens.border.thin,
    borderRadius: tokens.radius.sm,
    background: tokens.color.surface[0],
    padding: tokens.space[2],
    display: "flex",
    flexDirection: "column",
    gap: tokens.space[2],
    opacity: visible ? 1 : 0.6,
  };

  const isEnvLayer = layer.id.startsWith("layer:environment:") ||
    (layer.style as Record<string, unknown>)?.family === "environment";

  const handleZoom = () => {
    if (onZoomToLayer) onZoomToLayer(layer.id);
    else onFlyTo(layer);
  };

  const handleRemove = () => {
    if (!onRemoveLayer) return;
    if (typeof window === "undefined") return;
    const confirmed = window.confirm(
      `Remove "${layer.label ?? layer.id}" from this project?`,
    );
    if (!confirmed) return;
    // Synthesise a minimal UnifiedSceneTreeNode shape — onRemoveLayer only
    // reads the layer arg in compact mode (see TwinShell wiring).
    const fakeNode = {
      id: layer.id,
      label: layer.label ?? layer.id,
      kind: "layer",
      sceneItemIds: [layer.id],
    } as unknown as UnifiedSceneTreeNode;
    onRemoveLayer(fakeNode, layer);
  };

  const handleCardMenuAction = (id: string) => {
    if (id === "open-layer-tools") onOpenLayerTools?.(layer);
    if (id === "edit-metadata") onEditMetadata?.(layer);
    if (id === "save-layer-view") onSaveLayerView?.(layer);
    if (id === "solo-layer") onSoloLayer?.(layer.id);
    if (id === "remove-layer") {
      if (onRemoveLayerFromMenu) onRemoveLayerFromMenu(layer);
      else handleRemove();
    }
    setMenuOpen(false);
  };

  const menuItems: readonly MenuItemDef[] = [
    {
      id: "open-layer-tools",
      label: "Open layer tools",
      icon: "tune",
      disabled: !onOpenLayerTools,
    },
    {
      id: "edit-metadata",
      label: "Edit metadata",
      icon: "edit_note",
      disabled: !onEditMetadata,
    },
    {
      id: "save-layer-view",
      label: "Save view of this layer",
      icon: "bookmark",
      disabled: !onSaveLayerView,
    },
    {
      id: "solo-layer",
      label: "Solo this layer",
      icon: "filter_list",
      disabled: !onSoloLayer,
    },
    {
      id: "remove-layer",
      label: "Remove from project",
      icon: "delete",
      disabled: !onRemoveLayer && !onRemoveLayerFromMenu,
    },
  ];

  return (
    <article
      style={cardStyle}
      aria-label={`Layer ${layer.label ?? layer.id}`}
    >
      {/* Title row: health dot + title + remove */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: tokens.space[2],
          position: "relative",
        }}
      >
        <LayerHealthDot health={health} detail={unsupportedReason} />
        {isEnvLayer && onOpenLayerTools ? (
          <button
            type="button"
            onClick={() => onOpenLayerTools(layer)}
            aria-label={`Open tools for ${layer.label ?? layer.id}`}
            style={{
              flex: 1,
              minWidth: 0,
              padding: 0,
              border: "none",
              background: "transparent",
              textAlign: "left",
              cursor: "pointer",
            }}
          >
            <span
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                fontSize: tokens.font.size.sm,
                fontWeight: tokens.font.weight.medium,
                color: tokens.color.ink.primary,
                lineHeight: 1.25,
                wordBreak: "break-word",
              }}
              title={layer.label ?? layer.id}
            >
              {layer.label ?? layer.id}
            </span>
          </button>
        ) : (
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: tokens.font.size.sm,
              fontWeight: tokens.font.weight.medium,
              color: tokens.color.ink.primary,
              lineHeight: 1.25,
              wordBreak: "break-word",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
            title={layer.label ?? layer.id}
          >
            {layer.label ?? layer.id}
          </span>
        )}
        {lodLevel !== null ? (
          <span
            aria-label={`Level of detail ${lodLevel}`}
            title={`LOD ${lodLevel} — coarser detail tier`}
            style={{
              flexShrink: 0,
              padding: `0 ${tokens.space[1]}px`,
              border: tokens.border.thin,
              borderRadius: tokens.radius.sm,
              background: tokens.color.surface[2],
              color: tokens.color.ink.secondary,
              fontFamily: tokens.font.family.mono,
              fontSize: tokens.font.size.xs,
              fontWeight: tokens.font.weight.medium,
              lineHeight: 1.6,
              letterSpacing: 0.4,
            }}
          >
            LOD{lodLevel}
          </span>
        ) : null}
        <button
          type="button"
          onClick={() => onMoveLayer?.(layer.id, -1)}
          aria-label={`Move ${layer.label ?? layer.id} up`}
          disabled={!canMoveUp || !onMoveLayer}
          style={iconBtnStyle(!canMoveUp || !onMoveLayer)}
        >
          <Icon name="keyboard_arrow_up" size={16} decorative />
        </button>
        <button
          type="button"
          onClick={() => onMoveLayer?.(layer.id, 1)}
          aria-label={`Move ${layer.label ?? layer.id} down`}
          disabled={!canMoveDown || !onMoveLayer}
          style={iconBtnStyle(!canMoveDown || !onMoveLayer)}
        >
          <Icon name="swap_vert" size={16} decorative />
        </button>
        <button
          type="button"
          onClick={() => setMenuOpen((value) => !value)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          aria-label={`Open layer actions for ${layer.label ?? layer.id}`}
          style={iconBtnStyle()}
        >
          <Icon name="more_vert" size={16} decorative />
        </button>
        {menuOpen ? (
          <Menu
            aria-label={`Layer card actions for ${layer.label ?? layer.id}`}
            items={menuItems}
            onSelect={handleCardMenuAction}
            onClose={() => setMenuOpen(false)}
            style={{
              position: "absolute",
              right: 0,
              top: `calc(100% + ${tokens.space[1]}px)`,
              zIndex: tokens.zIndex.tooltip,
              minWidth: tokens.layout.popoverXs,
              border: tokens.border.thin,
              borderRadius: tokens.radius.md,
              background: tokens.color.surface[1],
              boxShadow: tokens.elevation[2],
              padding: tokens.space[1],
            }}
          />
        ) : null}
        {onRemoveLayer ? (
          <button
            type="button"
            onClick={handleRemove}
            aria-label={`Remove layer ${layer.label ?? layer.id}`}
            style={iconBtnStyle()}
          >
            <Icon name="delete" size={16} decorative />
          </button>
        ) : null}
      </div>

      {unsupportedReason ? (
        <p
          data-testid={`layer-unsupported-${layer.id}`}
          style={{
            margin: 0,
            padding: `0 ${tokens.space[2]}px ${tokens.space[1]}px`,
            fontSize: tokens.font.size.xs,
            color: tokens.color.ink.secondary,
            lineHeight: 1.35,
          }}
        >
          {unsupportedReason}
        </p>
      ) : null}

      {/* Metadata chip row */}
      {chips.length > 0 ? (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: tokens.space[1],
          }}
        >
          {chips.map((chip) => (
            <Chip
              key={chip.key}
              variant={chipToneToVariant(chip.tone)}
              label={`${chip.label}: ${chip.value}`}
            />
          ))}
        </div>
      ) : null}

      {/* Panorama-bubble "360° View" CTA */}
      {isPanoramaBubble && onOpenPanorama ? (
        <button
          type="button"
          onClick={() => onOpenPanorama(layer)}
          aria-label={`Open 360° panorama view for ${layer.label ?? layer.id}`}
          data-testid="panorama-open-btn"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: tokens.space[1],
            width: "100%",
            minHeight: tokens.density.touchMin,
            padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
            border: "none",
            borderRadius: tokens.radius.input,
            background: tokens.color.accent.primary,
            color: tokens.color.ink.inverse,
            cursor: "pointer",
            fontSize: tokens.font.size.sm,
            fontWeight: tokens.font.weight.semibold,
          }}
        >
          <Icon name="360" size={20} decorative />
          360° View
        </button>
      ) : null}

      {/* Action row: visibility + isolate + zoom */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: tokens.space[1],
        }}
      >
        <button
          type="button"
          onClick={() => onSetVisible(layer.id, !visible)}
          aria-label={visible ? "Hide layer" : "Show layer"}
          style={chipBtnStyle()}
        >
          <Icon
            name={visible ? "visibility_off" : "visibility"}
            size={16}
            decorative
          />
          {visible ? "Hide" : "Show"}
        </button>
        <button
          type="button"
          onClick={() => onSoloLayer?.(layer.id)}
          aria-label={`Isolate ${layer.label ?? layer.id} (hide every other layer)`}
          title="Isolate — show only this layer (click again to restore)"
          disabled={!onSoloLayer}
          style={chipBtnStyle()}
        >
          <Icon name="filter_center_focus" size={16} decorative />
          Isolate
        </button>
        {!isEnvLayer && (
          <button
            type="button"
            onClick={handleZoom}
            aria-label="Zoom to layer"
            disabled={isPanoramaBubble && layer.crs === "EPSG:UNKNOWN"}
            title={
              isPanoramaBubble && layer.crs === "EPSG:UNKNOWN"
                ? "Zoom unavailable — layer uses local engineering coordinates"
                : "Zoom to layer"
            }
            style={chipBtnStyle()}
          >
            <Icon name="my_location" size={16} decorative />
            Zoom
          </button>
        )}
      </div>

      {/* Opacity slider */}
      <label
        style={{
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          alignItems: "center",
          gap: tokens.space[2],
          fontSize: tokens.font.size.xs,
          color: tokens.color.ink.secondary,
        }}
      >
        <span>Opacity</span>
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={opacity}
          onChange={(event) =>
            onSetOpacity(layer.id, Number(event.currentTarget.value))
          }
          aria-label={`Opacity for ${layer.label ?? layer.id}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(opacity * 100)}
        />
        <span>{Math.round(opacity * 100)}%</span>
      </label>

      {/* Image adjustments (raster only) */}
      {isRaster && onSetRasterPaint ? (
        <div>
          <button
            type="button"
            onClick={() => setImageAdjustOpen((v) => !v)}
            aria-expanded={imageAdjustOpen}
            aria-controls={`image-adjust-${layer.id}`}
            style={{
              ...chipBtnStyle(),
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: tokens.space[1] }}>
              <Icon name="tune" size={16} decorative /> Image adjustments
            </span>
            <Icon
              name={imageAdjustOpen ? "keyboard_arrow_up" : "chevron_right"}
              size={16}
              decorative
            />
          </button>
          {imageAdjustOpen ? (
            <div
              id={`image-adjust-${layer.id}`}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: tokens.space[1],
                marginTop: tokens.space[2],
                paddingLeft: tokens.space[2],
                borderLeft: tokens.border.thin,
              }}
            >
              <RasterSlider
                label="Brightness"
                value={rasterPaint["raster-brightness-max"] ?? 1}
                onChange={(v) =>
                  onSetRasterPaint(layer.id, "raster-brightness-max", v)
                }
              />
              <RasterSlider
                label="Contrast"
                value={rasterPaint["raster-contrast"] ?? 0}
                min={-1}
                max={1}
                onChange={(v) =>
                  onSetRasterPaint(layer.id, "raster-contrast", v)
                }
              />
              <RasterSlider
                label="Saturation"
                value={rasterPaint["raster-saturation"] ?? 0}
                min={-1}
                max={1}
                onChange={(v) =>
                  onSetRasterPaint(layer.id, "raster-saturation", v)
                }
              />
            </div>
          ) : null}
        </div>
      ) : null}

      {isPointCloud ? (
        <>
          <div>
            <button
              type="button"
              onClick={() => setPointAppearanceOpen((value) => !value)}
              aria-expanded={pointAppearanceOpen}
              aria-controls={`point-appearance-${layer.id}`}
              style={{
                ...chipBtnStyle(),
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: tokens.space[1] }}>
                <Icon name="blur_on" size={16} decorative />
                Appearance
              </span>
              <Icon
                name={pointAppearanceOpen ? "keyboard_arrow_up" : "chevron_right"}
                size={16}
                decorative
              />
            </button>
            {pointAppearanceOpen ? (
              <div
                id={`point-appearance-${layer.id}`}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: tokens.space[1],
                  marginTop: tokens.space[2],
                }}
              >
                <label style={inlineSliderStyle()}>
                  <span>Point budget</span>
                  <input
                    type="range"
                    min={1}
                    max={100}
                    step={1}
                    value={pointBudgetMillions}
                    onChange={(event) => {
                      const next = Number(event.currentTarget.value);
                      setPointBudgetMillions(next);
                      onSetPointCloudAppearance?.(layer.id, {
                        pointBudget: next * 1_000_000,
                      });
                    }}
                    aria-label={`Point budget for ${layer.label ?? layer.id}`}
                    aria-valuemin={1}
                    aria-valuemax={100}
                    aria-valuenow={pointBudgetMillions}
                  />
                  <span>{pointBudgetMillions}M</span>
                </label>
                <label style={inlineSliderStyle()}>
                  <span>Point size</span>
                  <input
                    type="range"
                    min={0.5}
                    max={5}
                    step={0.1}
                    value={pointSize}
                    onChange={(event) => {
                      const next = Number(event.currentTarget.value);
                      setPointSize(next);
                      onSetPointCloudAppearance?.(layer.id, { pointSize: next });
                    }}
                    aria-label={`Point size for ${layer.label ?? layer.id}`}
                    aria-valuemin={0.5}
                    aria-valuemax={5}
                    aria-valuenow={pointSize}
                  />
                  <span>{pointSize.toFixed(1)}</span>
                </label>
                <div
                  role="group"
                  aria-label={`Point cloud color mode for ${layer.label ?? layer.id}`}
                  style={{ display: "flex", flexWrap: "wrap", gap: tokens.space[1] }}
                >
                  {POINT_CLOUD_COLOR_MODES.map((mode) => (
                    <button
                      key={mode.id}
                      type="button"
                      onClick={() => {
                        setPointColorMode(mode.id);
                        onSetPointCloudAppearance?.(layer.id, { colorMode: mode.id });
                      }}
                      aria-pressed={pointColorMode === mode.id}
                      style={chipBtnStyle(pointColorMode === mode.id)}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          <div>
            <button
              type="button"
              onClick={() => setPointClassificationOpen((value) => !value)}
              aria-expanded={pointClassificationOpen}
              aria-controls={`point-classes-${layer.id}`}
              style={{
                ...chipBtnStyle(),
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: tokens.space[1] }}>
                <Icon name="category" size={16} decorative />
                Classification
              </span>
              <Icon
                name={pointClassificationOpen ? "keyboard_arrow_up" : "chevron_right"}
                size={16}
                decorative
              />
            </button>
            {pointClassificationOpen ? (
              <div
                id={`point-classes-${layer.id}`}
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                  gap: tokens.space[1],
                  marginTop: tokens.space[2],
                }}
              >
                {ASPRS_CLASS_LABELS.map((entry) => {
                  const selected = selectedClasses.has(entry.id);
                  return (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => {
                        setSelectedClasses((prev) => {
                          const next = new Set(prev);
                          if (next.has(entry.id)) next.delete(entry.id);
                          else next.add(entry.id);
                          onSetPointCloudClassificationFilter?.(layer.id, next);
                          return next;
                        });
                      }}
                      aria-pressed={selected}
                      aria-label={`Toggle class ${entry.id} ${entry.label}`}
                      style={chipBtnStyle(selected)}
                    >
                      {entry.id}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
        </>
      ) : null}

      {isVector ? (
        <>
          <div>
            <button
              type="button"
              onClick={() => setVectorStyleOpen((value) => !value)}
              aria-expanded={vectorStyleOpen}
              aria-controls={`vector-style-${layer.id}`}
              style={{
                ...chipBtnStyle(),
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: tokens.space[1] }}>
                <Icon name="palette" size={16} decorative />
                Style
              </span>
              <Icon
                name={vectorStyleOpen ? "keyboard_arrow_up" : "chevron_right"}
                size={16}
                decorative
              />
            </button>
            {vectorStyleOpen ? (
              <div
                id={`vector-style-${layer.id}`}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: tokens.space[1],
                  marginTop: tokens.space[2],
                }}
              >
                <span style={sectionLabelStyle()}>Line color</span>
                <div style={{ display: "flex", gap: tokens.space[1] }}>
                  {VECTOR_COLOR_SWATCHES.map((swatch) => (
                    <button
                      key={`line-${swatch.id}`}
                      type="button"
                      onClick={() => {
                        setLineColor(swatch.value);
                        onSetVectorStyle?.(layer.id, { lineColor: swatch.value });
                      }}
                      aria-label={`Set line color to ${swatch.label}`}
                      style={swatchButtonStyle(lineColor === swatch.value, swatch.value)}
                    />
                  ))}
                </div>
                <label style={inlineSliderStyle()}>
                  <span>Line width</span>
                  <input
                    type="range"
                    min={1}
                    max={8}
                    step={1}
                    value={lineWidth}
                    onChange={(event) => {
                      const next = Number(event.currentTarget.value);
                      setLineWidth(next);
                      onSetVectorStyle?.(layer.id, { lineWidth: next });
                    }}
                    aria-label={`Line width for ${layer.label ?? layer.id}`}
                    aria-valuemin={1}
                    aria-valuemax={8}
                    aria-valuenow={lineWidth}
                  />
                  <span>{lineWidth}</span>
                </label>
                <span style={sectionLabelStyle()}>Fill color</span>
                <div style={{ display: "flex", gap: tokens.space[1] }}>
                  {VECTOR_COLOR_SWATCHES.map((swatch) => (
                    <button
                      key={`fill-${swatch.id}`}
                      type="button"
                      onClick={() => {
                        setFillColor(swatch.value);
                        onSetVectorStyle?.(layer.id, { fillColor: swatch.value });
                      }}
                      aria-label={`Set fill color to ${swatch.label}`}
                      style={swatchButtonStyle(fillColor === swatch.value, swatch.value)}
                    />
                  ))}
                </div>
                <label style={inlineSliderStyle()}>
                  <span>Fill opacity</span>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={fillOpacity}
                    onChange={(event) => {
                      const next = Number(event.currentTarget.value);
                      setFillOpacity(next);
                      onSetVectorStyle?.(layer.id, { fillOpacity: next });
                    }}
                    aria-label={`Fill opacity for ${layer.label ?? layer.id}`}
                    aria-valuemin={0}
                    aria-valuemax={1}
                    aria-valuenow={fillOpacity}
                  />
                  <span>{Math.round(fillOpacity * 100)}%</span>
                </label>
              </div>
            ) : null}
          </div>
          <div>
            <button
              type="button"
              onClick={() => setVectorLabelsOpen((value) => !value)}
              aria-expanded={vectorLabelsOpen}
              aria-controls={`vector-labels-${layer.id}`}
              style={{
                ...chipBtnStyle(),
                width: "100%",
                justifyContent: "space-between",
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: tokens.space[1] }}>
                <Icon name="label" size={16} decorative />
                Labels
              </span>
              <Icon
                name={vectorLabelsOpen ? "keyboard_arrow_up" : "chevron_right"}
                size={16}
                decorative
              />
            </button>
            {vectorLabelsOpen ? (
              <div
                id={`vector-labels-${layer.id}`}
                style={{
                  display: "grid",
                  gap: tokens.space[1],
                  marginTop: tokens.space[2],
                }}
              >
                <label
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: tokens.space[1],
                    fontSize: tokens.font.size.xs,
                    color: tokens.color.ink.secondary,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={labelsEnabled}
                    onChange={(event) => {
                      const next = event.currentTarget.checked;
                      setLabelsEnabled(next);
                      onSetVectorLabels?.(layer.id, {
                        enabled: next,
                        field: next ? (labelField || null) : null,
                      });
                    }}
                    aria-label={`Toggle labels for ${layer.label ?? layer.id}`}
                  />
                  Show labels
                </label>
                {vectorFields.length > 0 ? (
                  <select
                    value={labelField}
                    onChange={(event) => {
                      const nextField = event.currentTarget.value;
                      setLabelField(nextField);
                      onSetVectorLabels?.(layer.id, {
                        enabled: labelsEnabled,
                        field: nextField || null,
                      });
                    }}
                    aria-label={`Label field for ${layer.label ?? layer.id}`}
                    style={selectStyle()}
                  >
                    {vectorFields.map((field) => (
                      <option key={field} value={field}>
                        {field}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={labelField}
                    onChange={(event) => {
                      const nextField = event.currentTarget.value;
                      setLabelField(nextField);
                      onSetVectorLabels?.(layer.id, {
                        enabled: labelsEnabled,
                        field: nextField.trim().length > 0 ? nextField.trim() : null,
                      });
                    }}
                    aria-label={`Custom label field for ${layer.label ?? layer.id}`}
                    placeholder="property name"
                    style={textInputStyle()}
                  />
                )}
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </article>
  );
}

function RasterSlider({
  label,
  value,
  min = 0,
  max = 2,
  onChange,
}: {
  readonly label: string;
  readonly value: number;
  readonly min?: number;
  readonly max?: number;
  readonly onChange: (next: number) => void;
}) {
  return (
    <label
      style={{
        display: "grid",
        gridTemplateColumns: `${tokens.space[8] + tokens.space[4]}px 1fr auto`,
        alignItems: "center",
        gap: tokens.space[2],
        fontSize: tokens.font.size.xs,
        color: tokens.color.ink.secondary,
      }}
    >
      <span>{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={0.05}
        value={value}
        onChange={(event) => onChange(Number(event.currentTarget.value))}
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
      />
      <span>{value.toFixed(2)}</span>
    </label>
  );
}

function chipBtnStyle(pressed = false): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: tokens.space[1],
    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.sm,
    background: pressed ? tokens.color.accent.subtle : tokens.color.surface[2],
    color: pressed ? tokens.color.accent.primary : tokens.color.ink.primary,
    fontSize: tokens.font.size.xs,
    cursor: "pointer",
  };
}

function iconBtnStyle(disabled = false): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: tokens.space[5],
    height: tokens.space[5],
    border: "none",
    background: "transparent",
    color: disabled ? tokens.color.ink.muted : tokens.color.ink.secondary,
    cursor: disabled ? "not-allowed" : "pointer",
    borderRadius: tokens.radius.sm,
    opacity: disabled ? 0.6 : 1,
  };
}

function inlineSliderStyle(): CSSProperties {
  return {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    alignItems: "center",
    gap: tokens.space[2],
    fontSize: tokens.font.size.xs,
    color: tokens.color.ink.secondary,
  };
}

function sectionLabelStyle(): CSSProperties {
  return {
    fontSize: tokens.font.size.xs,
    color: tokens.color.ink.secondary,
  };
}

function swatchButtonStyle(selected: boolean, color: string): CSSProperties {
  return {
    width: tokens.space[4],
    height: tokens.space[4],
    border: tokens.border.thin,
    borderRadius: tokens.radius.sm,
    background: color,
    cursor: "pointer",
    boxShadow: selected ? tokens.elevation[1] : "none",
  };
}

function selectStyle(): CSSProperties {
  return {
    minHeight: tokens.density.touchMin,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[0],
    color: tokens.color.ink.primary,
    fontSize: tokens.font.size.sm,
    paddingInline: tokens.space[2],
  };
}

function textInputStyle(): CSSProperties {
  return {
    minHeight: tokens.density.touchMin,
    border: tokens.border.thin,
    borderRadius: tokens.radius.input,
    background: tokens.color.surface[0],
    color: tokens.color.ink.primary,
    fontSize: tokens.font.size.sm,
    paddingInline: tokens.space[2],
  };
}

/** Map `layerMetadata.ts` `ChipTone` ('danger') onto `ChipVariant` ('error'). */
function chipToneToVariant(
  tone: "neutral" | "info" | "success" | "warning" | "danger",
): ChipVariant {
  return tone === "danger" ? "error" : tone;
}
